// Which link clicks the unsaved-changes guard intercepts (POS comments item 2).
//
// The admin runs on <BrowserRouter>, so React Router's useBlocker (data routers
// only) is not available. Instead, while a form is dirty, one capture-phase
// click listener catches same-site links — sidebar NavLinks, page links and the
// plain <a> breadcrumbs — and asks before leaving. Anything the browser should
// handle itself (new tab, download, another site, a modified click) passes.

interface AnchorLike {
  getAttribute(name: string): string | null;
  hasAttribute(name: string): boolean;
  href: string;
}

interface ClickLike {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
  target: { closest?: (selector: string) => AnchorLike | null } | null;
}

/**
 * The in-app path a click would navigate to, when it should be confirmed first;
 * null when the click should go ahead untouched. A link to the page already
 * open (e.g. `?tab=` switches) is not leaving, so it is not intercepted.
 */
export function interceptedPath(e: ClickLike, current: { origin: string; pathname: string }): string | null {
  if (e.defaultPrevented || e.button !== 0) return null;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const anchor = e.target?.closest?.('a[href]');
  if (!anchor) return null;
  const target = anchor.getAttribute('target');
  if ((target && target !== '_self') || anchor.hasAttribute('download')) return null;
  let url: URL;
  try {
    url = new URL(anchor.href, current.origin);
  } catch {
    return null;
  }
  if (url.origin !== current.origin) return null;
  if (url.pathname === current.pathname) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}
